"use client"

import { useEffect, useState } from "react"
import { Clock, AlertCircle } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Alert, AlertDescription } from "@/components/ui/alert"

interface PartyDeletionCountdownProps {
  partyDate: string
  startTime?: string | null
  className?: string
}

export function PartyDeletionCountdown({ partyDate, startTime, className = "" }: PartyDeletionCountdownProps) {
  const [timeRemaining, setTimeRemaining] = useState<string>("")
  const [isExpiringSoon, setIsExpiringSoon] = useState(false)
  const [hasExpired, setHasExpired] = useState(false)

  useEffect(() => {
    const calculateTimeRemaining = () => {
      // Calculate deletion time: party date + 48 hours
      const partyDateTime = new Date(partyDate)

      // If there's a start time, use it; otherwise assume end of day
      if (startTime) {
        const [hours, minutes] = startTime.split(":").map(Number)
        partyDateTime.setHours(hours, minutes, 0, 0)
      } else {
        // If no start time, assume party ends at 11:59 PM
        partyDateTime.setHours(23, 59, 59, 999)
      }

      // Add 48 hours to get deletion time
      const deletionTime = new Date(partyDateTime.getTime() + 48 * 60 * 60 * 1000)
      const now = new Date()
      const diff = deletionTime.getTime() - now.getTime()

      if (diff <= 0) {
        setHasExpired(true)
        setTimeRemaining("Party will be deleted soon")
        return
      }

      // Calculate time components
      const hours = Math.floor(diff / (1000 * 60 * 60))
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((diff % (1000 * 60)) / 1000)

      // Check if expiring within 6 hours
      setIsExpiringSoon(hours < 6)

      // Format the time remaining
      if (hours >= 24) {
        const days = Math.floor(hours / 24)
        const remainingHours = hours % 24
        setTimeRemaining(`${days}d ${remainingHours}h`)
      } else if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m`)
      } else if (minutes > 0) {
        setTimeRemaining(`${minutes}m ${seconds}s`)
      } else {
        setTimeRemaining(`${seconds}s`)
      }
    }

    // Calculate immediately
    calculateTimeRemaining()

    // Update every second
    const interval = setInterval(calculateTimeRemaining, 1000)

    return () => clearInterval(interval)
  }, [partyDate, startTime])

  if (hasExpired) {
    return (
      <Alert variant="destructive" className={className}>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription className="text-sm">
          This party has ended and will be automatically deleted soon. Chat history will be removed.
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Badge variant={isExpiringSoon ? "destructive" : "secondary"} className="flex items-center gap-1.5 px-3 py-1">
        <Clock className="w-3.5 h-3.5" />
        <span className="text-xs font-medium">Auto-deletes in {timeRemaining}</span>
      </Badge>
      {isExpiringSoon && (
        <span className="text-xs text-muted-foreground">Party and chat will be removed after 48h</span>
      )}
    </div>
  )
}
